<template>
  <div class="flex flex-col gap-6">
    <WarningDisable permission="nmap" />
    <div class="grow">
      <div class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <label class="form-control">
          <span class="label-text">Mục tiêu (CIDR / dải IP)</span>
          <input
            v-model="target"
            type="text"
            class="input input-bordered"
            placeholder="10.0.0.0/24"
          />
        </label>
        <label class="form-control">
          <span class="label-text">Module Net Scan</span>
          <select v-model="selectedVersion" class="select select-bordered" :disabled="versionsLoading">
            <option value="">{{ versionsLoading ? "Đang tải…" : "Chọn module" }}</option>
            <option v-for="item in versions" :key="item.id" :value="item.id">{{ item.version }} — {{ item.file_name }}</option>
          </select>
        </label>
        <label class="form-control">
          <span class="label-text flex items-center gap-2">
            Chế độ quét
            <span
              class="tooltip tooltip-right"
              data-tip="Nhanh: TCP tới 8 cổng phổ biến. Đầy đủ: TCP tới 32 cổng định sẵn, không phải toàn bộ 65535 cổng. Không quét ICMP hay gửi ARP chủ động; SMB chỉ được kiểm tra bằng TCP/445. Thụ động: không probe cổng, nhưng vẫn ghi nhận IP dải quét và đọc route/ARP cache hiện có của máy agent."
            >
              <i
                class="fa-solid fa-circle-info cursor-help text-base-content/60"
                role="img"
                aria-label="Thông tin về giao thức và chế độ quét"
              ></i>
            </span>
          </span>
          <select v-model="scanType" class="select select-bordered">
            <option v-for="mode in scanModes" :key="mode.value" :value="mode.value">{{ mode.label }}</option>
          </select>
        </label>
        <label class="form-control">
          <span class="label-text">Cường độ (1–5)</span>
          <select v-model.number="intensity" class="select select-bordered">
            <option v-for="n in 5" :key="n" :value="n">{{ n }}</option>
          </select>
        </label>
      </div>
      <p v-if="versionsError" class="text-error">Không tải được danh sách module Net Scan. <button class="link" @click="loadVersions">Thử lại</button></p>
      <p v-else-if="!versionsLoading && !versions.length" class="text-warning">Hãy tải module Net Scan lên trong Quản lý phiên bản trước.</p>
      <div class="mb-2 flex items-baseline justify-between">
        <div class="text-base-content/80 mb-2 text-xl font-bold">
          Lịch sử quét
        </div>
        <button
          class="btn btn-primary"
          @click="handleStartScan"
          :disabled="action_store.loading.create_action || !selectedVersion || !target.trim() || versionsLoading || !versions.length"
        >
          <span
            v-if="action_store.loading.create_action"
            class="loading loading-spinner"
          ></span>
          <i v-else class="fa-solid fa-search mr-2"></i>
          Bắt đầu quét
        </button>
      </div>
      <Loading v-if="action_store.loading.get_action_history" />
      <template v-else-if="action_store.total_filter > 0">
        <div class="bg-base-100 mb-4 overflow-x-auto">
          <table class="table-pin-rows table-md table">
            <thead>
              <tr>
                <th></th>
                <th>ID Tác vụ</th>
                <th>Chế độ quét</th>
                <th>Trạng thái</th>
                <th>Thời gian tạo</th>
              </tr>
            </thead>
            <tbody class="bg-base-200">
              <tr
                v-for="(action, idx) in action_store.actions"
                :key="action.id"
                class="hover:bg-base-300 cursor-pointer transition-colors"
                @click="
                  $router.push({
                    name: 'ClientNmapScanDetail',
                    params: {
                      action_id: action.id,
                    },
                  })
                "
              >
                <th>
                  {{
                    idx +
                    1 +
                    (number_from_query("page", 1) - 1) *
                      number_from_query("size", 24)
                  }}
                </th>
                <td class="text-xs">
                  {{ action.id }}
                </td>
                <td>
                  {{ scanModes.find((mode) => mode.value === action.nmap_option?.scan_type)?.label ?? "Không rõ" }}
                </td>
                <td>
                  <ActionStatusBadge :status="action.status" />
                </td>
                <td class="whitespace-nowrap">
                  {{ dayjs(action.created).fromNow() }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Pagination :total_pages="action_store.total_pages" />
      </template>
      <NotFound v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import { axios_instance } from "@/api/api";
import type { version } from "@/components/VersionControl";
import { number_from_query } from "@/api";
import ActionStatusBadge from "@/components/ActionStatusBadge.vue";
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import Pagination from "@/components/PaginationTemplate.vue";
import dayjs from "dayjs";
import { onMounted, ref } from "vue";
import { toast } from "vue3-toastify";
import { use_action } from "../ExplorerV2/useAction";
import WarningDisable from "../WarningDisable.vue";

const action_store = use_action();
const versions = ref<version[]>([]);
const versionsLoading = ref(false);
const versionsError = ref(false);
const selectedVersion = ref("");
const scanModes = [
  { value: "quick", label: "Nhanh" },
  { value: "full", label: "Đầy đủ" },
  { value: "passive", label: "Thụ động" },
];
const scanType = ref("quick");
const intensity = ref(1);
const target = ref("");

const loadVersions = async () => {
  versionsLoading.value = true;
  versionsError.value = false;
  try {
    const response = await axios_instance.post<{ data: version[] | null; total_filter: number }>("/api/v1/view/version/pagination", { type: ["net_scan"], page: 1, size: 100 });
    versions.value = response.data.data ?? [];
  } catch {
    versionsError.value = true;
  } finally {
    versionsLoading.value = false;
  }
};

onMounted(() => {
  loadData();
  loadVersions();
});

const loadData = () => {
  action_store.get_action_history({
    type: "netscan",
    action: "scan",
  });
};

const handleStartScan = async () => {
  if (!selectedVersion.value || !target.value.trim()) return;
  try {
    const actionId = await action_store.create_action({
      type: "netscan",
      action: "scan",
      version: selectedVersion.value,
      ip: target.value.trim(),
      nmap_option: { scan_type: scanType.value, intensity: intensity.value },
    });
    if (actionId) {
      toast.success("Đã gửi yêu cầu quét mạng!");
      setTimeout(() => {
        loadData();
      }, 1000);
    }
  } catch (error) {
    console.error("Error starting scan:", error);
    toast.error("Không thể bắt đầu quét mạng!");
  }
};
</script>
